import { Component, createRef } from 'react';
import { observable, action, makeObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import apiService from '@services/apiService';
import { modalService } from '@components/Modal';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import { ImgThumbsUp } from '@images/ImgThumbsUp';
import { ImgThumbsDown } from '@images/ImgThumbsDown';
import { ImgRefresh } from '@images/ImgRefresh';
import { ImageWrapper } from '@components/ImageWrapper';
import VideoThumb from '@components/VideoThumb';

type ChatSuggestionsProps = {
  onSelect: (message: string, capability: string, imageFile?: File | null) => void;
  onImageSuggestionSelect?: (item: ImageSuggestionItem) => void;
};

type SuggestionItem = {
  id?: string;
  title: string;
  text?: string;
  capability?: 'vision' | 'tools' | 'none';
  isLiked?: boolean;
}

export type ImageSuggestionItem = {
  id: string;
  url: string;
  prompt: string;
  model: string;
  kind: 't2i' | 'i2i' | 'i2v' | 't2v';
  lora: string | null;
  orientation: 'square' | 'portrait' | 'landscape' | null;
  sourceImageUrl: string | null;
  isVideo: boolean;
  isLiked?: boolean;
  likedCount?: number;
  dislikedCount?: number;
  selectedCount?: number;
};

const greetingMessages = {
  general: [
    '{user}, how can I assist you today?',
    '{user}, what can I help you with?',
    '{user}, how can I make your day easier?',
    '{user}, what would you like to talk about?',
    '{user}, how can I support you today?',
    '{user}, what chaos shall we create today?',
    '{user}, what\'s on your mind?',
    'Welcome back, {user}!'],
  morning: ['{user}, good morning! How can I assist you today?',
    'Good morning, {user}!',
    'Morning! {user}, what can I help you with?',
    '{user}, how can I make your morning better?',
    'Good morning {user}, coffee in hand?',
    'Morning {user}, let\'s make it a good one',
    'Morning {user}, may your coffee be perfect today',
    '{user}, fresh start, fresh energy',
    '{user}, the sun is up, you should probably be too',
    'Morning, {user}!'
  ],
  afternoon: ['Good afternoon, {user}! How can I assist you today?',
    'Good afternoon, {user}!',
    'Afternoon! {user}, what can I help you with?',
    '{user}, hope you\'re enjoying your day',
    '{user}, how can I make your afternoon better?'
  ],
  evening: [
    'Good evening, {user}! How can I assist you tonight?',
    'Good evening, {user}',
    'Evening! {user}, what can I help you with?',
    '{user}, how can I make your evening better?',
    '{user}, what would you like to talk about this evening?',
    '{user}, how can I support you tonight?'
  ],
  night: [
    'Working late, {user}? How can I assist you tonight?',
    'Night owl! {user}, what can I help you with?',
    '{user}, how can I make your night better?',
    'Late night {user}, I\'m here with you',
    '{user}, what would you like to talk about tonight?',
    '{user}, how can I support you tonight?'
  ]
}

@observer
export default class ChatSuggestions extends Component<ChatSuggestionsProps> {

  @observable private _suggestions: SuggestionItem[] = [];
  @observable private _imageSuggestions: ImageSuggestionItem[] = [];
  private _fileInputRef = createRef<HTMLInputElement>();
  private _pendingVisionSuggestion: SuggestionItem | null = null;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _imageLoader: PromiseCompletion = new PromiseCompletion();
  private _greeting = '';

  constructor(props: ChatSuggestionsProps) {
    super(props);
    makeObservable(this);


    const userName = window.user?.givenName || window.user?.name;
    const hour = new Date().getHours();
    let greetingCategory = 'general';
    if (hour >= 5 && hour < 12) {
      greetingCategory = 'morning';
    } else if (hour >= 12 && hour < 17) {
      greetingCategory = 'afternoon';
    } else if (hour >= 17 && hour < 22) {
      greetingCategory = 'evening';
    } else {
      greetingCategory = 'night';
    }

    // 20% - general greeting, 80% - time based greeting
    if (Math.random() < 0.2) {
      greetingCategory = 'general';
    }
    const greetings = greetingMessages[greetingCategory as keyof typeof greetingMessages];
    // name of user can be not available due to privacy settings. If it's available - update greeting to include it, if not - just use generic greeting
    const greeting = greetings[Math.floor(Math.random() * greetings.length)];
    // replace ', {user}' or '{user} ,' with user name of empty string if name is not available. Then spli by ?.! and trim + uppercase first letter
    let personalizedGreeting = greeting.replace(/,?\s*\{user\}\s*,?/, userName ? `, ${userName},` : '').split(/([.?!])/).map(part => {
      // trim [, ] characters from start and end of part, then uppercase first letter and join with rest of string. If part is only punctuation - return as is
      const trimmed = part.trim().replace(/^[,]+|[,]+$/g, '');
      return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : '';
    }).join(' ');

    //replace ' ?' and ' !' with '?' and '!' respectively
    this._greeting = personalizedGreeting.replace(/\s+([?!])/g, '$1');
  }

  componentDidMount(): void {
    void this._loadSuggestions();
    void this._loadImageSuggestions();
  }

  render() {
    return (
      <div className="chat-starter">
        <div className="starter-title-row">
          <h2>{this._greeting}</h2>
          <button
            className="starter-refresh-btn"
            title="Refresh suggestions"
            onClick={() => this._onRefreshClick()}
          >
            <ImgRefresh size={18} />
          </button>
        </div>
        {!!this._suggestions.length && <div className="starter-suggestions">
            {this._suggestions.map((item, index) => (
            <div key={item.id || index} className={`suggestion-item${item.isLiked ? ' liked' : ''}`}>
              <button
                  className="suggestion-btn"
                  onClick={() => this._onSuggestionClick(item)}
              >
                  {item.title}
              </button>
              {item.id && (
                <div className="suggestion-actions">
                  <button
                    className={`suggestion-action-btn like-btn${item.isLiked ? ' active' : ''}`}
                    onClick={(e) => this._onLikeClick(e, item)}
                    title={item.isLiked ? 'Unlike' : 'Like'}
                  >
                    <ImgThumbsUp size={14} liked={item.isLiked} />
                  </button>
                  <button
                    className="suggestion-action-btn dislike-btn"
                    onClick={(e) => this._onDislikeClick(e, item)}
                    title="Dislike"
                  >
                    <ImgThumbsDown size={14} />
                  </button>
                </div>
              )}
            </div>
            ))}
        </div>}
        {!!this._imageSuggestions.length && <div className="starter-image-suggestions">
          {this._imageSuggestions.map(item => (
            <div
              key={item.id}
              className={`starter-image-item${item.isLiked ? ' liked' : ''}`}
              title={this._imageHint(item)}
              onClick={() => this._onImageSuggestionClick(item)}
            >
              {item.isVideo
                ? <VideoThumb src={item.url} className="starter-image-thumb starter-image-video" />
                : <ImageWrapper
                    className="starter-image-thumb"
                    src={item.url}
                    previewSrc={`${item.url}/preview?width=100`}
                    alt={item.prompt}
                    onThumbnailClick={() => {}}
                  />}
              <span className="starter-image-kind">{this._kindLabel(item)}</span>
              <div className="starter-image-votes">
                <button
                  className={`suggestion-action-btn like-btn${item.isLiked ? ' active' : ''}`}
                  title={item.isLiked ? 'Unlike' : 'Like'}
                  aria-label={item.isLiked ? 'Unlike image suggestion' : 'Like image suggestion'}
                  aria-pressed={!!item.isLiked}
                  onClick={(e) => this._onImageLikeClick(e, item)}
                >
                  <ImgThumbsUp size={14} liked={!!item.isLiked} />
                </button>
                <button
                  className="suggestion-action-btn dislike-btn"
                  title="Dislike"
                  onClick={(e) => this._onImageDislikeClick(e, item)}
                >
                  <ImgThumbsDown size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>}
        <input
          type="file"
          ref={this._fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={this._onImageSelected}
        />
      </div>
    );
  }

  private _imageHint(item: ImageSuggestionItem): string {
    const parts = [item.prompt];
    const meta: string[] = [];
    meta.push(item.model);
    if (item.lora) meta.push(`LoRA: ${item.lora}`);
    if (item.orientation) meta.push(item.orientation);
    if (item.sourceImageUrl) meta.push('uses a reference image');
    parts.push(meta.join(' · '));
    return parts.join('\n');
  }

  private _kindLabel(item: ImageSuggestionItem): string {
    switch (item.kind) {
      case 't2i': return 'Text→Image';
      case 'i2i': return 'Image→Image';
      case 't2v': return 'Text→Video';
      case 'i2v': return 'Image→Video';
      default: return item.kind;
    }
  }

  private _onSuggestionClick = (item: SuggestionItem) => {
    if (item.capability === 'vision') {
      // For vision suggestions, open file picker first (selection recorded once a file is chosen)
      this._pendingVisionSuggestion = item;
      this._fileInputRef.current?.click();
    } else {
      void this._recordTextSelect(item);
      this.props.onSelect(item.text || item.title, item.capability || '');
    }
  };

  private _recordTextSelect(item: SuggestionItem): Promise<unknown> {
    if (!item.id) return Promise.resolve();
    return apiService.post(`/api/suggestions/${item.id}/select`, {}, { suppressToast: true }).catch(error => {
      console.error('Failed to record text suggestion selection:', error);
    });
  }

  private _onLikeClick = async (e: React.MouseEvent, item: SuggestionItem) => {
    e.stopPropagation();
    if (!item.id) return;
    const response = await apiService.post<{ success: boolean; isLiked: boolean }>(`/api/suggestions/${item.id}/like`, {}, { loader: this._loader });
    if (response.success) {
      runInAction(() => {
        const suggestion = this._suggestions.find(s => s.id === item.id);
        if (suggestion) {
          suggestion.isLiked = response.isLiked;
        }
      });
    }
  };

  private _onDislikeClick = async (e: React.MouseEvent, item: SuggestionItem) => {
    e.stopPropagation();
    if (!item.id) return;

    if (!await modalService.showConfirmation('Are you sure you want to dislike this suggestion? It will be removed.', 'Dislike Suggestion', { buttonTitle: 'Dislike', color: 'danger' })) {
      return;
    }
    await apiService.post<{ success: boolean; deleted: boolean }>(`/api/suggestions/${item.id}/dislike`, {}, { loader: this._loader });
    
    // Remove from local state
    runInAction(() => {
      this._suggestions = this._suggestions.filter(s => s.id !== item.id);
    });

    // Load one more suggestion to replace
    void this._loadSuggestions(1);
  };

  private _onImageSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && this._pendingVisionSuggestion) {
      void this._recordTextSelect(this._pendingVisionSuggestion);
      this.props.onSelect(
        this._pendingVisionSuggestion.text || this._pendingVisionSuggestion.title, 
        this._pendingVisionSuggestion.capability || '',
        file
      );
    }
    this._pendingVisionSuggestion = null;
    // Reset input so same file can be selected again
    if (this._fileInputRef.current) {
      this._fileInputRef.current.value = '';
    }
  };

  @action
  private async _loadSuggestions(maxCount: number = 5) {
    const response = await apiService.get<{ suggestions: SuggestionItem[] }>(`/api/suggestions?maxCount=${maxCount}`, { loader: this._loader });

    runInAction(() => {
      if (maxCount === 1) {
        // Append single suggestion
        this._suggestions.push(...response.suggestions);
      } else {
        this._suggestions = response.suggestions;
      }
    });
  }

  private _onRefreshClick = () => {
    void this._loadSuggestions();
    void this._loadImageSuggestions();
  };

  private _onImageSuggestionClick = (item: ImageSuggestionItem) => {
    void this._recordImageSelect(item);
    this.props.onImageSuggestionSelect?.(item);
  };

  private _onImageLikeClick = async (e: React.MouseEvent, item: ImageSuggestionItem) => {
    e.stopPropagation();
    if (!item.id) return;
    const response = await apiService.post<{ success: boolean; isLiked: boolean }>(`/api/suggestions/images/${item.id}/like`, {}, { loader: this._imageLoader });
    if (response.success) {
      runInAction(() => {
        const img = this._imageSuggestions.find(s => s.id === item.id);
        if (img) {
          img.isLiked = response.isLiked;
          img.likedCount = Math.max(0, (img.likedCount ?? 0) + (response.isLiked ? 1 : -1));
        }
      });
    }
  };

  private _onImageDislikeClick = async (e: React.MouseEvent, item: ImageSuggestionItem) => {
    e.stopPropagation();
    if (!item.id) return;

    if (!await modalService.showConfirmation('Are you sure you want to dislike this suggestion? It will be ranked lower.', 'Dislike Suggestion', { buttonTitle: 'Dislike', color: 'danger' })) {
      return;
    }
    const response = await apiService.post<{ success: boolean; isDisliked: boolean }>(`/api/suggestions/images/${item.id}/dislike`, {}, { loader: this._imageLoader });
    if (response.success) {
      runInAction(() => {
        const img = this._imageSuggestions.find(s => s.id === item.id);
        if (img) {
          img.dislikedCount = Math.max(0, (img.dislikedCount ?? 0) + (response.isDisliked ? 1 : -1));
          img.likedCount = Math.max(0, (img.likedCount ?? 0) - (response.isDisliked ? 1 : 0));
        }
      });
    }
  };

  private _recordImageSelect(item: ImageSuggestionItem): Promise<unknown> {
    if (!item.id) return Promise.resolve();
    return apiService.post(`/api/suggestions/images/${item.id}/select`, {}, { suppressToast: true }).catch(error => {
      console.error('Failed to record image suggestion selection:', error);
    });
  }

  @action
  private async _loadImageSuggestions() {
    const response = await apiService.get<{ suggestions: ImageSuggestionItem[] }>(`/api/suggestions/images?count=5`, { loader: this._imageLoader });
    runInAction(() => {
      this._imageSuggestions = response.suggestions;
    });
  }

}